// The T&M rate lines — on a bid they ARE the Time & Material quote (a crew + equipment
// day rate, a drill day/hour rate, a truck hour rate, a unit price, a mobilization); on
// the job they are the T&M Rates the field logs against. Same rows: what's priced on the
// quote carries onto the job at award, nothing is re-entered.
//
// Each line has a rate and a quantity. On the quote the quantity is an optional ESTIMATE
// (leave it blank and the quote stays open-ended, "$7,500 per day"); on the job it is what
// actually happened, and the line bills rate × quantity against the Not-to-Exceed.
//
// Lines are picked from the catalogs we already keep — Drill Rates, the Rate Sheet
// (labor / trucks / equipment by the hour), the Parts database — or typed as custom.
import React, { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../../services/apiClient';
import { TM_SOURCE_LABEL } from './ExtraWorkCard';

const money = n => (n == null || n === '') ? '—' : '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const num = v => (v === '' || v == null) ? null : Number(v);

// The fixed menu of what a T&M line can be.
export const TM_TYPES = [
  { key: 'day',          label: 'Day rate',     unit: 'day',  tint: '#1d4ed8', bg: '#eff6ff' },
  { key: 'hour',         label: 'Hour rate',    unit: 'hr',   tint: '#7c3aed', bg: '#f5f3ff' },
  { key: 'unit',         label: 'Unit price',   unit: 'unit', tint: '#0e7490', bg: '#ecfeff' },
  { key: 'mobilization', label: 'Mobilization', unit: 'trip', tint: '#b45309', bg: '#fffbeb' },
];
const TYPE = Object.fromEntries(TM_TYPES.map(t => [t.key, t]));
export const tmUnitOf = it => it.type === 'unit' ? (it.unit_label || 'ea') : (TYPE[it.type] || TYPE.day).unit;
const lineTotal = it => (it.rate != null && it.quantity != null) ? Number(it.rate) * Number(it.quantity) : 0;

const SOURCES = [
  // Common Items = the drilling rates + the Price Book's items (Pat, Sep 2026). They
  // were two pills — "Drill Rates" and "Standard Items" — until he made them one.
  ['common', 'Common Items'],
  ['rates',  'Rate Sheet'],
  ['parts',  'Parts'],
  ['custom', 'Custom'],
];

// Tell the rest of the page (tile previews, the quote header) the list changed.
const announce = () => window.dispatchEvent(new CustomEvent('titan:tm-items-changed'));

export default function TmTile({ projectId, bidId, bid, project = null, canEdit = false, compact = false, quoteMode = false }) {
  const base = bidId ? `/bids/${bidId}/tm-items` : `/projects/${projectId}/tm-items`;
  const [items, setItems] = useState(null);
  // A work order under an on-call contract (2633, Sep 2026): the contract's rate lines
  // come back flagged inherited — linked, shown apart, changed on the contract — and the
  // work order says which group leads on its card.
  const [inherited, setInherited] = useState([]);
  const isWo = !!(project && project.parent_project_id);
  const [leads, setLeads] = useState(project?.tm_rate_source || null);
  useEffect(() => { setLeads(project?.tm_rate_source || null); }, [project?.tm_rate_source]);
  const [parentNum, setParentNum] = useState(null);
  useEffect(() => {
    if (!isWo) return;
    apiFetch(`/projects/${project.parent_project_id}`).then(p => setParentNum(p?.project_number || null)).catch(() => {});
  }, [isWo, project?.parent_project_id]);
  // A contract: how many work orders these rates reach.
  const [woCount, setWoCount] = useState(0);
  useEffect(() => {
    if (!projectId || isWo || !project || project.division === 'small') return;
    apiFetch(`/projects/${projectId}/work-orders`).then(r => setWoCount(Array.isArray(r) ? r.length : 0)).catch(() => {});
  }, [projectId, isWo, project]);
  useEffect(() => {
    const on = e => { if (e.detail && e.detail.projectId === projectId) setLeads(e.detail.source || null); };
    window.addEventListener('titan:tm-rate-source-changed', on);
    return () => window.removeEventListener('titan:tm-rate-source-changed', on);
  }, [projectId]);
  const [adding, setAdding] = useState(false);
  const [source, setSource] = useState('common');
  const [q, setQ] = useState('');
  const [draft, setDraft] = useState({ type: 'day', name: '', rate: '', unit_label: '' });
  const [catalog, setCatalog] = useState({ drills: null, rates: null, parts: null, standard: null });

  const load = useCallback(() => {
    apiFetch(base).then(r => { const all = Array.isArray(r) ? r : []; setItems(all.filter(x => !x.inherited)); setInherited(all.filter(x => x.inherited)); }).catch(() => setItems([]));
  }, [base]);
  useEffect(() => { load(); }, [load]);

  // The catalogs load once the picker opens — not before, most visits never add a line.
  useEffect(() => {
    if (!adding) return;
    if (source === 'common' && catalog.drills === null) apiFetch('/drill-rates').then(r => setCatalog(c => ({ ...c, drills: Array.isArray(r) ? r : [] }))).catch(() => setCatalog(c => ({ ...c, drills: [] })));
    if (source === 'rates' && catalog.rates === null) apiFetch('/rate-sheet').then(r => setCatalog(c => ({ ...c, rates: Array.isArray(r?.items) ? r.items : [] }))).catch(() => setCatalog(c => ({ ...c, rates: [] })));
    if (source === 'parts' && catalog.parts === null) apiFetch('/parts').then(r => setCatalog(c => ({ ...c, parts: Array.isArray(r) ? r : [] }))).catch(() => setCatalog(c => ({ ...c, parts: [] })));
    if (source === 'common' && catalog.standard === null) apiFetch('/standard-items').then(r => setCatalog(c => ({ ...c, standard: Array.isArray(r) ? r : [] }))).catch(() => setCatalog(c => ({ ...c, standard: [] })));
  }, [adding, source, catalog.drills, catalog.rates, catalog.parts, catalog.standard]);

  const [nte, setNte] = useState(bid?.tm_nte ?? '');
  useEffect(() => { setNte(bid?.tm_nte ?? ''); }, [bid?.tm_nte]);
  const total = (items || []).reduce((s, it) => s + lineTotal(it), 0);
  const anyQty = (items || []).some(it => Number(it.quantity) > 0);
  const nteNum = num(nte);
  // A cap is a positive number or nothing. A stray scroll over the box can leave "-30"
  // in it; that clears rather than saving as a negative cap. The updated bid goes back
  // to the page, which used to keep its stale copy — so a cleared cap kept showing on
  // the tile preview (and came back into this box) until the page was reloaded.
  const saveNte = async v => {
    const n = Number(v);
    const clean = v === '' || v == null || !Number.isFinite(n) || n <= 0 ? null : n;
    setNte(clean ?? '');
    if (!bid?.id) return;
    try {
      const updated = await apiFetch(`/bids/${bid.id}/tm`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ nte: clean }) });
      announce();
      if (updated && updated.id) window.dispatchEvent(new CustomEvent('titan:bid-changed', { detail: updated }));
    } catch { /* keep the typed value */ }
  };

  // Optimistic field edit → PATCH; revert on failure.
  const patch = async (it, field, value) => {
    const prev = it[field];
    setItems(list => list.map(x => x.id === it.id ? { ...x, [field]: value } : x));
    try { await apiFetch(`${base}/${it.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ [field]: value }) }); announce(); }
    catch { setItems(list => list.map(x => x.id === it.id ? { ...x, [field]: prev } : x)); }
  };
  const add = async body => {
    try {
      const row = await apiFetch(base, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      setItems(list => [...(list || []), row]);
      announce();
      return true;
    } catch { return false; }
  };
  const addCustom = async () => {
    const body = { type: draft.type, name: draft.name.trim() || TYPE[draft.type].label, rate: draft.rate, unit_label: draft.unit_label.trim() || null, source_type: 'custom' };
    if (await add(body)) { setDraft({ type: 'day', name: '', rate: '', unit_label: '' }); setAdding(false); }
  };
  const del = async it => {
    if (!window.confirm(`Remove "${it.name}"?`)) return;
    setItems(list => list.filter(x => x.id !== it.id));
    apiFetch(`${base}/${it.id}`, { method: 'DELETE' }).then(announce).catch(load);
  };
  // Reorder: swap with the neighbour, then write every line's position (older lists
  // may share a sort_order, so the whole order is made explicit rather than trusting
  // two swapped numbers). Arrows rather than drag — they work the same on a phone.
  const move = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = items.slice();
    [next[i], next[j]] = [next[j], next[i]];
    const renumbered = next.map((it, idx) => ({ ...it, sort_order: idx }));
    setItems(renumbered);
    try {
      await Promise.all(renumbered.filter((it, idx) => items.find(o => o.id === it.id)?.sort_order !== idx)
        .map(it => apiFetch(`${base}/${it.id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ sort_order: it.sort_order }) })));
      announce();
    } catch { load(); }
  };

  if (items === null) return <div style={{ fontSize: 13, color: '#9ca3af' }}>Loading…</div>;

  const cell = { fontSize: 13, border: '1px solid #e5e7eb', borderRadius: 6, padding: '5px 7px', fontFamily: 'inherit' };
  const overCap = nteNum != null && total > nteNum;
  const qtyLabel = quoteMode ? 'Est. qty' : 'Qty';
  const showQty = quoteMode;   // on the job the lines are rates, set once — the days carry the quantities
  const lbl = { fontSize: 10.5, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4 };
  const pick = { display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 13, cursor: 'pointer', textAlign: 'left', width: '100%', font: 'inherit' };
  const match = s => !q.trim() || String(s || '').toLowerCase().includes(q.trim().toLowerCase());

  const fromNum = inherited[0]?.inherited_from || parentNum || 'the contract';
  return (
    <div>
      {!quoteMode && project?.id && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 10, fontSize: 12.5, color: '#6b7280' }}>
          <span style={{ fontSize: 10.5, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4 }}>Price structure</span>
          <span style={{ padding: '3px 10px', borderRadius: 999, background: leads ? '#f5f3ff' : '#f3f4f6', color: leads ? '#6d28d9' : '#6b7280', fontWeight: 800, fontSize: 12 }}>{TM_SOURCE_LABEL[leads] || 'not picked yet'}</span>
          <span>· set on the Job header · these lines lead on the Project T&M Items tile and the card</span>
        </div>
      )}
      {isWo && (
        <div style={{ border: '1px solid #99f6e4', background: '#f0fdfa', borderRadius: 10, padding: '10px 12px', marginBottom: 12 }}>
          <div style={{ marginTop: 0 }}>
            <div style={{ fontSize: 10.5, fontWeight: 800, color: '#0f766e', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>
              From {fromNum} <span style={{ fontWeight: 600, color: '#6b7280', textTransform: 'none', letterSpacing: 0 }}>· linked — change them on that job’s T&amp;M Rates and every work order follows</span>
            </div>
            {inherited.length === 0
              ? <div style={{ fontSize: 12.5, color: '#6b7280' }}>Nothing on {fromNum}’s T&amp;M Rates yet. Pick the Rate Sheet lines there and they show on every work order under it.</div>
              : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {inherited.map(it => {
                    const t = TYPE[it.type] || TYPE.day;
                    return (
                      <div key={it.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13 }}>
                        <span style={{ flex: 1, fontWeight: 700, color: '#111', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.name}</span>
                        <span style={{ fontSize: 10, fontWeight: 800, color: t.tint, background: t.bg, borderRadius: 4, padding: '1px 6px' }}>{t.label}</span>
                        <span style={{ fontWeight: 700 }}>{it.rate != null ? `${money(it.rate)}/${tmUnitOf(it)}` : `per ${tmUnitOf(it)}`}</span>
                      </div>
                    );
                  })}
                </div>
              )}
          </div>
        </div>
      )}
      {!isWo && !quoteMode && woCount > 0 && (items || []).length > 0 && (
        <div style={{ fontSize: 12.5, color: '#0f766e', background: '#f0fdfa', border: '1px solid #99f6e4', borderRadius: 8, padding: '7px 10px', marginBottom: 10 }}>
          These rates carry to every work order under this job — {woCount} so far — linked, so a change here reaches them all.
        </div>
      )}
      {/* Running total (an estimate on the quote), and the Not-to-Exceed cap right here. */}
      {showQty && <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        {anyQty || !quoteMode ? (
          <>
            <span style={{ fontSize: 22, fontWeight: 800, color: overCap ? '#b91c1c' : '#111' }}>{money(total)}</span>
            {quoteMode && <span style={{ fontSize: 11, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4 }}>estimated</span>}
          </>
        ) : (
          <span style={{ fontSize: 13, color: '#6b7280' }}>Open-ended — rates only. Enter estimated quantities to show an estimated total on the quote.</span>
        )}
        <span style={{ fontSize: 12.5, color: overCap ? '#b91c1c' : '#9ca3af', fontWeight: overCap ? 700 : 400, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          {overCap ? '⚠ over the ' : (anyQty || !quoteMode) ? 'of ' : ''}
          {canEdit
            ? <>$<input type="number" step="any" min="0" value={nte} onChange={e => setNte(e.target.value)}
                onBlur={e => saveNte(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }} placeholder="—"
                style={{ ...cell, width: 90, padding: '3px 6px' }} title="Not-to-exceed cap (optional) — leave blank for open-ended" /></>
            : <span>{nteNum != null ? money(nteNum) : '—'}</span>}
          not-to-exceed{quoteMode ? ' (optional)' : ''}
        </span>
      </div>}

      {(items.length === 0) ? (
        <div style={{ fontSize: 13, color: '#9ca3af', marginBottom: 12 }}>
          {quoteMode ? 'No quote items yet. Add the rates this quote goes out with — a drill day rate, a truck by the hour, a mobilization, a unit price.' : 'No T&M items yet. Pick a Price Structure on the Job header and its lines land here, or add a day rate, an hour rate, a unit price or a mobilization below.'}
        </div>
      ) : (
        <div style={{ border: '1px solid #eef0f2', borderRadius: 10, overflow: 'hidden', marginBottom: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: `${canEdit ? '30px ' : ''}1.7fr 0.9fr ${showQty ? '0.7fr 0.9fr ' : ''}24px`, gap: 8, padding: '7px 12px', background: '#f8fafc', fontSize: 10.5, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4 }}>
            {canEdit && <span />}<span>Item</span><span style={{ textAlign: 'right' }}>Rate</span>{showQty && <span style={{ textAlign: 'right' }}>{qtyLabel}</span>}{showQty && <span style={{ textAlign: 'right' }}>Line</span>}<span />
          </div>
          {items.map((it, i) => {
            const t = TYPE[it.type] || TYPE.day;
            const qty = Number(it.quantity) || 0;
            return (
              <div key={it.id} style={{ display: 'grid', gridTemplateColumns: `${canEdit ? '30px ' : ''}1.7fr 0.9fr ${showQty ? '0.7fr 0.9fr ' : ''}24px`, gap: 8, alignItems: 'center', padding: '8px 12px', borderTop: i ? '1px solid #f1f3f5' : 'none' }}>
                {canEdit && (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    {[[-1, '▲', 'Move up', i === 0], [1, '▼', 'Move down', i === items.length - 1]].map(([dir, glyph, title, off]) => (
                      <button key={dir} onClick={() => move(i, dir)} disabled={off} title={title}
                        style={{ background: 'none', border: 'none', padding: '0 4px', lineHeight: 1, fontSize: 11, color: off ? '#e5e7eb' : '#9ca3af', cursor: off ? 'default' : 'pointer' }}
                        onMouseEnter={e => { if (!off) e.currentTarget.style.color = '#6d28d9'; }} onMouseLeave={e => { e.currentTarget.style.color = off ? '#e5e7eb' : '#9ca3af'; }}>
                        {glyph}
                      </button>
                    ))}
                  </div>
                )}
                <div style={{ minWidth: 0 }}>
                  {canEdit
                    ? <input defaultValue={it.name} onBlur={e => e.target.value.trim() !== it.name && patch(it, 'name', e.target.value.trim())} style={{ ...cell, width: '100%', fontWeight: 700 }} />
                    : <span style={{ fontSize: 13, fontWeight: 700, color: '#111' }}>{it.name}</span>}
                  <span style={{ display: 'inline-block', marginTop: 3, fontSize: 10, fontWeight: 800, color: t.tint, background: t.bg, borderRadius: 4, padding: '1px 6px' }}>
                    {t.label}{it.type === 'unit' && it.unit_label ? ` · /${it.unit_label}` : ''}
                  </span>
                </div>
                <div style={{ textAlign: 'right' }}>
                  {canEdit
                    ? <input type="number" step="any" defaultValue={it.rate ?? ''} onBlur={e => patch(it, 'rate', num(e.target.value))} style={{ ...cell, width: 84, textAlign: 'right' }} />
                    : <span style={{ fontSize: 13 }}>{money(it.rate)}</span>}
                  <div style={{ fontSize: 10.5, color: '#9ca3af' }}>per {tmUnitOf(it)}</div>
                </div>
                {showQty && <div style={{ textAlign: 'right' }}>
                  {canEdit
                    ? <input type="number" step="any" defaultValue={it.quantity || ''} placeholder={quoteMode ? '—' : '0'} onBlur={e => patch(it, 'quantity', num(e.target.value) ?? 0)} style={{ ...cell, width: 62, textAlign: 'right' }} />
                    : <span style={{ fontSize: 13 }}>{qty || (quoteMode ? '—' : 0)}</span>}
                </div>}
                {showQty && <span style={{ textAlign: 'right', fontSize: 13, fontWeight: 700, color: qty ? '#111' : '#9ca3af' }}>
                  {qty ? money(lineTotal(it)) : (quoteMode ? `per ${tmUnitOf(it)}` : money(0))}
                </span>}
                {canEdit
                  ? <button onClick={() => del(it)} title="Remove" style={{ background: 'none', border: 'none', color: '#d1d5db', fontSize: 16, cursor: 'pointer', lineHeight: 1 }}
                      onMouseEnter={e => e.currentTarget.style.color = '#dc2626'} onMouseLeave={e => e.currentTarget.style.color = '#d1d5db'}>×</button>
                  : <span />}
              </div>
            );
          })}
        </div>
      )}

      {canEdit && (adding ? (
        <div style={{ border: '1.5px solid #ddd6fe', background: '#faf5ff', borderRadius: 10, padding: 12 }}>
          {/* Where the line comes from. Catalog picks land straight on the list with the
              catalog's rate; you can still edit the rate on the line afterwards. */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10, alignItems: 'center' }}>
            {SOURCES.map(([k, l]) => (
              <button key={k} onClick={() => { setSource(k); setQ(''); }}
                style={{ padding: '6px 12px', borderRadius: 999, border: `1.5px solid ${source === k ? '#6d28d9' : '#e5e7eb'}`, background: source === k ? '#6d28d9' : '#fff', color: source === k ? '#fff' : '#374151', fontSize: 12.5, fontWeight: 800, cursor: 'pointer' }}>
                {l}
              </button>
            ))}
            <button onClick={() => { setAdding(false); setQ(''); }} style={{ marginLeft: 'auto', padding: '6px 12px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 12.5, color: '#6b7280', cursor: 'pointer' }}>Close</button>
          </div>

          {source === 'common' && (
            catalog.drills === null ? <div style={{ fontSize: 13, color: '#9ca3af' }}>Loading drill rates…</div> : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8 }}>
                {catalog.drills.map(d => (
                  <div key={d.drill} style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 10, background: '#fff' }}>
                    <div style={{ fontSize: 13, fontWeight: 800, color: '#92400e', marginBottom: 6 }}>🛠️ {d.drill}</div>
                    {[['day', 'Day rate', d.daily_rate], ['hour', 'Hour rate', d.hourly_rate], ['mobilization', 'Mobilization', d.mobilization]].map(([type, label, rate]) => (
                      <button key={type} disabled={rate == null} onClick={() => add({ type, name: type === 'mobilization' ? `${d.drill} mobilization` : `${d.drill} drilling — ${type}`, rate, source_type: 'drill_rate', source_id: d.drill })}
                        style={{ ...pick, marginTop: 4, opacity: rate == null ? 0.45 : 1, cursor: rate == null ? 'default' : 'pointer' }}>
                        <span style={{ flex: 1 }}>{label}</span>
                        <span style={{ fontWeight: 800 }}>{rate == null ? 'not set' : `${money(rate)}/${TYPE[type].unit}`}</span>
                        {rate != null && <span style={{ color: '#6d28d9', fontWeight: 800 }}>+</span>}
                      </button>
                    ))}
                  </div>
                ))}
                <div style={{ gridColumn: '1 / -1', fontSize: 11.5, color: '#9ca3af' }}>Drilling rates come from Settings → Price Book → Common Items; a rate that reads "not set" needs a number there first.</div>
              </div>
            )
          )}

          {/* The Price Book's items (the other half of Common Items) — loops per foot or each, pole hold by the day,
              mobilization. ft / ea land as a unit price in that unit; day, hr and trip as
              the matching rate type. No rate in the book yet? It still lands; type it on
              the line. */}
          {source === 'common' && (
            catalog.standard === null ? <div style={{ fontSize: 13, color: '#9ca3af' }}>Loading the Price Book…</div> : (
              <div>
                <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search common items — loops, pole hold, mobilization…" style={{ ...cell, width: '100%', boxSizing: 'border-box', marginBottom: 8 }} />
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 6, maxHeight: 260, overflowY: 'auto' }}>
                  {catalog.standard.filter(s => match(s.name)).map(s => {
                    const type = s.unit === 'day' ? 'day' : s.unit === 'hr' ? 'hour' : s.unit === 'trip' ? 'mobilization' : 'unit';
                    return (
                      <button key={s.id} onClick={() => add({ type, name: s.name, unit_label: type === 'unit' ? s.unit : null, rate: s.rate, source_type: 'standard_item', source_id: s.id })} style={pick}>
                        <span style={{ flex: 1, fontWeight: 600 }}>{s.name}</span>
                        <span style={{ fontWeight: 800, color: s.rate == null ? '#b45309' : undefined }}>{s.rate == null ? `set on the line / ${s.unit}` : `${money(s.rate)}/${s.unit}`}</span>
                        <span style={{ color: '#6d28d9', fontWeight: 800 }}>+</span>
                      </button>
                    );
                  })}
                  {catalog.standard.length === 0 && <div style={{ fontSize: 13, color: '#9ca3af' }}>No common items yet — add them in Settings → Price Book.</div>}
                </div>
                <div style={{ fontSize: 11.5, color: '#9ca3af', marginTop: 6 }}>From Settings → Price Book. The rate lands on the line and can be changed for this customer.</div>
              </div>
            )
          )}

          {source === 'rates' && (
            catalog.rates === null ? <div style={{ fontSize: 13, color: '#9ca3af' }}>Loading the rate sheet…</div> : (
              <div>
                <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search labor, trucks, equipment…" style={{ ...cell, width: '100%', boxSizing: 'border-box', marginBottom: 8 }} />
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 6, maxHeight: 260, overflowY: 'auto' }}>
                  {catalog.rates.filter(r => match(`${r.category} ${r.description}`)).map(r => (
                    <button key={r.id} onClick={() => add({ type: 'hour', name: r.description, rate: r.rate, source_type: 'rate_sheet', source_id: r.id })} style={pick}>
                      <span style={{ fontSize: 10, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', width: 70 }}>{r.category}</span>
                      <span style={{ flex: 1, fontWeight: 600 }}>{r.description}</span>
                      <span style={{ fontWeight: 800 }}>{money(r.rate)}/{String(r.unit || 'HR').toLowerCase()}</span>
                      <span style={{ color: '#6d28d9', fontWeight: 800 }}>+</span>
                    </button>
                  ))}
                  {catalog.rates.length === 0 && <div style={{ fontSize: 13, color: '#9ca3af' }}>The rate sheet is empty — fill it in from the Rate Sheet button on the Bids page.</div>}
                </div>
              </div>
            )
          )}

          {source === 'parts' && (
            catalog.parts === null ? <div style={{ fontSize: 13, color: '#9ca3af' }}>Loading parts…</div> : (
              <div>
                <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search the parts database…" autoFocus style={{ ...cell, width: '100%', boxSizing: 'border-box', marginBottom: 8 }} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 260, overflowY: 'auto' }}>
                  {catalog.parts.filter(p => match(`${p.description} ${p.category || ''}`)).slice(0, 40).map(p => (
                    <button key={p.id} onClick={() => add({ type: 'unit', name: p.description, unit_label: (p.unit || 'ea').toLowerCase(), rate: p.material_cost, source_type: 'part', source_id: p.id })} style={pick}>
                      <span style={{ flex: 1, fontWeight: 600 }}>{p.description}</span>
                      {p.category && <span style={{ fontSize: 10, fontWeight: 800, color: '#3730a3', background: '#e0e7ff', borderRadius: 4, padding: '1px 6px' }}>{p.category}</span>}
                      <span style={{ fontWeight: 800 }}>{money(p.material_cost)}/{(p.unit || 'ea').toLowerCase()}</span>
                      <span style={{ color: '#6d28d9', fontWeight: 800 }}>+</span>
                    </button>
                  ))}
                  {catalog.parts.length > 0 && !q.trim() && <div style={{ fontSize: 11.5, color: '#9ca3af' }}>Showing the first 40 — type to search.</div>}
                </div>
                <div style={{ fontSize: 11.5, color: '#9ca3af', marginTop: 6 }}>Lands at the part's material cost — edit the rate on the line to set what you're charging.</div>
              </div>
            )
          )}

          {source === 'custom' && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <span style={lbl}>Type</span>
                <select value={draft.type} onChange={e => setDraft(d => ({ ...d, type: e.target.value }))} style={{ ...cell }}>
                  {TM_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1, minWidth: 160 }}>
                <span style={lbl}>Name</span>
                <input value={draft.name} placeholder="e.g. Crew + equipment" onChange={e => setDraft(d => ({ ...d, name: e.target.value }))} style={{ ...cell }} />
              </label>
              {draft.type === 'unit' && (
                <label style={{ display: 'flex', flexDirection: 'column', gap: 3, width: 90 }}>
                  <span style={lbl}>Per</span>
                  <input value={draft.unit_label} placeholder="loop, ft…" onChange={e => setDraft(d => ({ ...d, unit_label: e.target.value }))} style={{ ...cell }} />
                </label>
              )}
              <label style={{ display: 'flex', flexDirection: 'column', gap: 3, width: 120 }}>
                <span style={lbl}>Rate ($)</span>
                <input type="number" step="any" value={draft.rate} placeholder="10000" onChange={e => setDraft(d => ({ ...d, rate: e.target.value }))} style={{ ...cell }} />
              </label>
              <button onClick={addCustom} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#6d28d9', color: '#fff', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>Add</button>
            </div>
          )}
        </div>
      ) : (
        <button onClick={() => setAdding(true)} style={{ padding: '8px 16px', borderRadius: 9, border: '1.5px dashed #c4b5fd', background: '#faf5ff', color: '#6d28d9', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>
          + Add {quoteMode ? 'quote item' : 'T&M item'}
        </button>
      ))}
    </div>
  );
}
